import React, { useCallback, useEffect, useMemo } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import ConditionalWrapper from 'components/ConditionalWrapper'
import { Table } from 'components/TableV1'
import { SearchBar, Label } from 'components/Input'
import { useInput } from 'hooks/useInput'
import { fetchCarriers, updateSearch } from 'features/carriers/actions'
import {
  carriersIsLoadingSelector,
  carriersQuerySelector,
  carriersSelector,
} from 'features/carriers/selectors'
import {
  carriersInvitedMapSelector,
  carrierInvitesMapSelector,
} from 'features/carrierInvites/selectors'

import AddNewCarrier from './AddNewCarrier'
import CarrierRow from './CarrierRow'
import { LabelP, Loader } from './styles'
import { RFP_STATE } from '../../constants'

const ScrollerDiv = styled.div.attrs({
  'data-testid': 'scroller',
})`
  height: 300px;
  overflow-y: auto;
`

function carrierInList(carrier: Carrier, carriersList: Carrier[]) {
  return carriersList.map(({ id }) => id).includes(carrier.id)
}

interface Props {
  rfpState?: string
  selectedCarriers: Carrier[]
  isLoading?: boolean
  rfpId?: number
  toggleCarrier(carrier: Carrier, isSelected: boolean): void
}

export default function InviteCarriers({
  rfpState,
  selectedCarriers,
  toggleCarrier,
  rfpId,
  isLoading: isLoadingProp,
}: Props) {
  const dispatch = useDispatch()
  const carriers = useSelector(carriersSelector)
  const carriersQuery = useSelector(carriersQuerySelector)
  const carrierInvitesMap: Record<string, CarrierInvite> = useSelector(
    carrierInvitesMapSelector
  )
  const carriersInvitedMap = useSelector(carriersInvitedMapSelector)
  const isLoading = useSelector(carriersIsLoadingSelector)
  const {
    onChange: carrierSearchOnChange,
    onClear: carrierSearchOnClear,
  } = useInput(carriersQuery)

  const sortedCarriers = useMemo(() => {
    return carriers
      .map((carrier) => {
        const isInvited = carrierInList(carrier, selectedCarriers)
        const carrierInvite = isInvited
          ? carriersInvitedMap[carrier.id]
          : undefined
        return {
          ...carrier,
          isInvited,
          invite: carrierInvite?.invite,
        }
      })
      .sort((a, b) => {
        return a.isInvited && b.isInvited ? 0 : a.isInvited ? -1 : 1
      })
  }, [carriers, selectedCarriers, carriersInvitedMap])

  const [debounced] = useDebouncedCallback(() => {
    dispatch(fetchCarriers())
  }, 450)

  const handleCarrierSearchChange = useCallback(
    (event) => {
      carrierSearchOnChange(event)
      dispatch(updateSearch(event.target.value))
    },
    [carrierSearchOnChange, dispatch]
  )

  const handleCarrierSearchClear = useCallback(() => {
    carrierSearchOnClear()
    dispatch(updateSearch(''))
  }, [carrierSearchOnClear, dispatch])

  const isEmailSent = useCallback(
    (inviteId?: number) => {
      if (carrierInvitesMap && inviteId) {
        // TODO Index by carrier invite id instead of the shipper. See https://bit.ly/2SHL7nW

        const invite = Object.values(carrierInvitesMap).find(
          ({ id }) => id === inviteId
        )
        return !!invite?.email_sent_at
      }
      return false
    },
    [carrierInvitesMap]
  )

  const rfpIsClosedForProposals = rfpState
    ? rfpState === RFP_STATE.CLOSED || rfpState === RFP_STATE.FINALIZED
    : false

  useEffect(() => {
    dispatch(updateSearch(''))
  }, [dispatch])

  useEffect(() => {
    debounced()
  }, [carriersQuery, debounced])

  return (
    <>
      <Label>
        {rfpIsClosedForProposals ? (
          <LabelP>SEARCH CARRIERS</LabelP>
        ) : (
          <LabelP>INVITE CARRIERS</LabelP>
        )}
        <SearchBar
          data-testid="query"
          onChange={handleCarrierSearchChange}
          onClear={handleCarrierSearchClear}
          placeholder="Search"
          value={carriersQuery}
        />
      </Label>
      <ConditionalWrapper wrapper={ScrollerDiv} condition={!rfpId}>
        <Table>
          <tbody>
            {sortedCarriers.map((carrier) => (
              <CarrierRow
                carrier={carrier}
                disabled={
                  isEmailSent(carrier.invite) || rfpIsClosedForProposals
                }
                isInvited={carrier.isInvited}
                key={carrier.id}
                showAcceptanceState={!!rfpId}
                toggle={() => toggleCarrier(carrier, carrier.isInvited)}
              />
            ))}
          </tbody>
        </Table>
      </ConditionalWrapper>
      {!rfpIsClosedForProposals && (
        <AddNewCarrier toggleCarrier={toggleCarrier} rfpId={rfpId} />
      )}
      {(isLoading || isLoadingProp) && (
        <Loader type="Oval" height={50} width={50} />
      )}
    </>
  )
}
